import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class RegionDetectionService {
  private regionMapping = {
    // South America - Must be checked FIRST due to timezone overlap with US
    'southamerica-east': [
      'America/Sao_Paulo',
      'America/Argentina/Buenos_Aires',
      'America/Montevideo',
      'America/Santiago',
      'America/Asuncion',
      'America/Bogota',
      'America/Caracas',
      'America/Lima',
      'America/La_Paz',
      'America/Guayaquil',
      'America/Manaus',
      'America/Fortaleza',
      'America/Recife',
      'America/Bahia',
      'America/Campo_Grande',
      'America/Cuiaba',
      'America/Porto_Velho',
      'America/Boa_Vista',
      'America/Santarem',
      'America/Araguaina',
      'America/Belem',
      'America/Maceio',
    ],

    // United States and North America
    'us-central': [
      'America/Chicago',
      'America/Denver',
      'America/Boise',
      'America/Phoenix',
      'America/Mexico_City',
      'America/Monterrey',
      'America/Cancun',
      'America/Chihuahua',
      'America/Hermosillo',
      'America/Mazatlan',
      'America/Merida',
      'America/Tijuana',
    ],
    'us-east': [
      'America/New_York',
      'America/Detroit',
      'America/Indiana/Indianapolis',
      'America/Kentucky/Louisville',
      'America/Toronto',
      'America/Montreal',
      'America/Halifax',
      'America/Boston',
      'America/Washington',
      'America/Philadelphia',
      'America/Atlanta',
      'America/Miami',
      'America/Nassau',
      'America/Jamaica',
    ],
    'us-west': [
      'America/Los_Angeles',
      'America/Vancouver',
      'America/Tijuana',
      'America/Dawson_Creek',
      'America/Seattle',
      'America/Portland',
      'America/San_Francisco',
      'America/Las_Vegas',
      'America/Anchorage',
    ],

    // Europe
    'europe-west': [
      'Europe/London',
      'Europe/Dublin',
      'Europe/Madrid',
      'Europe/Paris',
      'Europe/Rome',
      'Europe/Berlin',
      'Europe/Amsterdam',
      'Europe/Brussels',
      'Europe/Zurich',
      'Europe/Vienna',
      'Europe/Prague',
      'Europe/Warsaw',
      'Europe/Stockholm',
      'Europe/Oslo',
      'Europe/Copenhagen',
      'Europe/Helsinki',
      'Europe/Lisbon',
    ],

    // Asia
    'asia-south': [
      'Asia/Kolkata',
      'Asia/Mumbai',
      'Asia/Delhi',
      'Asia/Dhaka',
      'Asia/Karachi',
      'Asia/Kathmandu',
      'Asia/Colombo',
    ],
    'asia-northeast': [
      'Asia/Tokyo',
      'Asia/Seoul',
      'Asia/Shanghai',
      'Asia/Beijing',
      'Asia/Hong_Kong',
      'Asia/Taipei',
    ],
    'asia-southeast': [
      'Asia/Singapore',
      'Asia/Jakarta',
      'Asia/Bangkok',
      'Asia/Ho_Chi_Minh',
      'Asia/Manila',
      'Asia/Kuala_Lumpur',
    ],

    // Australia and Oceania
    'australia-southeast': [
      'Australia/Sydney',
      'Australia/Melbourne',
      'Australia/Brisbane',
      'Australia/Adelaide',
      'Australia/Hobart',
      'Australia/Perth',
      'Australia/Darwin',
      'Pacific/Auckland',
      'Pacific/Fiji',
    ],
  };

  constructor() {}

  /**
   * Detects the most appropriate region based on user's timezone
   */
  detectRegionByTimezone(): string {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Search for the region that corresponds to the user's timezone
      for (const [region, timezones] of Object.entries(this.regionMapping)) {
        if (timezones.includes(userTimezone)) {
          return region;
        }
      }

      // If no exact match is found, try partial matches
      for (const [region, timezones] of Object.entries(this.regionMapping)) {
        for (const timezone of timezones) {
          if (userTimezone.includes(timezone.split('/')[0])) {
            return region;
          }
        }
      }
    } catch (error) {
      // Silent fallback - no console.warn needed in production
    }

    // Default value if detection fails
    return 'us-central';
  }

  /**
   * Detects region using browser geolocation (as fallback)
   */
  async detectRegionByGeolocation(): Promise<string> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(this.detectRegionByTimezone());
        return;
      }

      const timeoutId = setTimeout(() => {
        resolve(this.detectRegionByTimezone());
      }, 5000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          const { latitude, longitude } = position.coords;
          const region = this.getRegionByCoordinates(latitude, longitude);
          resolve(region);
        },
        (error) => {
          clearTimeout(timeoutId);
          resolve(this.detectRegionByTimezone());
        },
        {
          timeout: 5000,
          enableHighAccuracy: false,
        }
      );
    });
  }

  /**
   * Maps geographic coordinates to regions
   */
  private getRegionByCoordinates(lat: number, lng: number): string {
    // United States and North America
    if (lat >= 25 && lat <= 49 && lng >= -125 && lng <= -66) {
      if (lng >= -125 && lng <= -115) return 'us-west';
      if (lng >= -115 && lng <= -95) return 'us-central';
      if (lng >= -95 && lng <= -66) return 'us-east';
    }

    // Europe
    if (lat >= 35 && lat <= 71 && lng >= -10 && lng <= 40) {
      return 'europe-west';
    }

    // South Asia
    if (lat >= 5 && lat <= 35 && lng >= 68 && lng <= 97) {
      return 'asia-south';
    }

    // Northeast Asia
    if (lat >= 20 && lat <= 50 && lng >= 100 && lng <= 145) {
      return 'asia-northeast';
    }

    // Southeast Asia
    if (lat >= -10 && lat <= 25 && lng >= 95 && lng <= 140) {
      return 'asia-southeast';
    }

    // Australia and Oceania
    if (lat >= -45 && lat <= -10 && lng >= 110 && lng <= 180) {
      return 'australia-southeast';
    }

    // South America
    if (lat >= -55 && lat <= 12 && lng >= -82 && lng <= -35) {
      return 'southamerica-east';
    }

    // Default fallback to timezone detection
    return this.detectRegionByTimezone();
  }

  /**
   * Main method to detect region
   * Tries timezone first, then geolocation as fallback
   */
  async detectRegion(): Promise<string> {
    try {
      // Try timezone first (faster and more reliable)
      const timezoneRegion = this.detectRegionByTimezone();

      // If timezone gives a specific region other than us-central, use it
      if (timezoneRegion !== 'us-central') {
        return timezoneRegion;
      }

      // If it's us-central or couldn't detect well, try geolocation
      return await this.detectRegionByGeolocation();
    } catch (error) {
      return 'us-central'; // Final fallback
    }
  }

  /**
   * Testing method - simulates different timezones
   * For development/testing purposes only
   */
  testWithTimezone(mockTimezone: string): string {
    const originalMethod = this.detectRegionByTimezone.bind(this);

    // Temporarily override the method
    this.detectRegionByTimezone = () => {
      for (const [region, timezones] of Object.entries(this.regionMapping)) {
        if (timezones.includes(mockTimezone)) {
          return region;
        }
      }
      return 'us-central';
    };

    const result = this.detectRegionByTimezone();

    // Restore original method
    this.detectRegionByTimezone = originalMethod;

    return result;
  }
}
